import React, { useState } from 'react';
import { formatCurrency } from '../../../../utils/format';
import { formatDate } from '../../../../utils/date';
import { useTenant } from "../../../../hooks/useTenant";
import { FINANCIAL_TYPES } from '../../../../constants/financial';
import { NeoBadge, NeoCard, NeoFinancialTable } from '../../../../components/Common';

export const CashierTable = ({ records, loading, summary, selectedDate, currentShift, onCloseCashier }) => {
    const { user } = useTenant();
    const [isOpen, setIsOpen] = useState(true);
    const [cashierSearch, setCashierSearch] = useState("");

    const toggleOpen = () => setIsOpen(!isOpen);

    const userName = (currentShift?.operatorName || user?.name || user?.displayName || currentShift?.operatorId || "CAIXA PRINCIPAL").toUpperCase();
    const openDate = currentShift?.openedAt?.toDate
        ? formatDate(currentShift.openedAt.toDate())
        : selectedDate.split('-').reverse().join('/');

    // ── Consolidação de registros (agrupamento por venda + forma de pagamento) ──
    const consolidatedRecords = React.useMemo(() => {
        if (!records) return [];
        const groups = {};
        const result = [];

        records.forEach(r => {
            if (r.idSale && r.idSale.trim() !== '') {
                const key = `${r.idSale}_${r.paymentMethod}`;
                if (!groups[key]) {
                    groups[key] = {
                        ...r,
                        isGrouped: false,
                        groupedCount: 1,
                        totalAmount: r.netAmount || 0
                    };
                    result.push(groups[key]);
                } else {
                    groups[key].totalAmount += (r.netAmount || 0);
                    groups[key].groupedCount += 1;
                    groups[key].isGrouped = true;
                    const descParts = groups[key].description?.split('—') || [];
                    const baseDesc = descParts[0]?.trim() || `Venda ${r.idSale.substring(r.idSale.length - 6)}`;
                    groups[key].description = `${baseDesc} — Parcelado (${groups[key].groupedCount}x)`;
                    groups[key].dueDate = null;
                }
            } else {
                result.push({ ...r, totalAmount: r.netAmount || 0 });
            }
        });

        result.forEach(item => {
            if (item.isGrouped) {
                item.netAmount = item.totalAmount;
            }
        });

        return result;
    }, [records]);

    // ── Filtro de busca local ──
    const filteredRecords = React.useMemo(() => {
        if (!cashierSearch) return consolidatedRecords;
        const term = cashierSearch.toLowerCase().trim();
        return consolidatedRecords.filter(r =>
            (r.memberName || r.entityName || r.description || '').toLowerCase().includes(term) ||
            (r.paymentMethod || '').toLowerCase().includes(term) ||
            (r.description || '').toLowerCase().includes(term)
        );
    }, [cashierSearch, consolidatedRecords]);

    // ── Colunas para o NeoFinancialTable ──
    const cashierColumns = [
        {
            key: 'name',
            label: 'Nome',
            render: (row, rowIndex, { page, rowsPerPage }) => {
                const indexNum = String((page - 1) * rowsPerPage + rowIndex + 1).padStart(2, '0');
                const name = row.memberName?.toUpperCase() || row.entityName?.toUpperCase() || row.description?.toUpperCase() || '-';
                return (
                    <div>
                        <span className="fw-bold d-block text-brand-primary font-size-12">
                            <span className="text-brand-secondary me-2">{indexNum}.</span>
                            {name}
                        </span>
                    </div>
                );
            }
        },
        {
            key: 'paymentMethod',
            label: 'Forma de Pagamento',
            width: '170px',
            render: (row) => (
                <span className="font-size-12 text-muted">
                    <i className="mdi mdi-cash text-muted me-1" />
                    {row.paymentMethod || 'Diversos'}
                </span>
            )
        },
        {
            key: 'description',
            label: 'Descrição',
            render: (row) => (
                <span className="font-size-12 text-muted">
                    {row.description || '-'}
                </span>
            )
        },
        {
            key: 'paymentDate',
            label: 'Lançamento',
            width: '120px',
            render: (row) => (
                <span className="fw-semibold font-size-12 text-dark">
                    {formatDate(row.paymentDate)}
                </span>
            )
        },
        {
            key: 'dueDate',
            label: 'Vencimento',
            width: '120px',
            render: (row) => (
                <span className="font-size-12 text-muted">
                    {row.isGrouped ? '-' : formatDate(row.dueDate)}
                </span>
            )
        },
        {
            key: 'type',
            label: 'Tipo',
            width: '120px',
            align: 'center',
            render: (row) => {
                const isIncome = row.type === FINANCIAL_TYPES.ASSET;
                return (
                    <NeoBadge color={isIncome ? 'success' : 'danger'}>
                        {isIncome ? 'Recebimento' : 'Saída'}
                    </NeoBadge>
                );
            }
        },
        {
            key: 'netAmount',
            label: 'Valor',
            width: '130px',
            align: 'right',
            render: (row) => (
                <span className="fw-bold font-size-13 text-dark">
                    {formatCurrency(row.netAmount)}
                </span>
            )
        }
    ];

    // ── Renderização dos detalhes expandidos ──
    const renderRowDetails = (row) => (
        <div className="neo-cashier-row-details">
            <div className="text-muted font-size-11 fw-bold text-uppercase d-flex align-items-center gap-3">
                <span><i className="mdi mdi-calendar me-1"></i>Data da venda: {formatDate(row.paymentDate)}</span>
                <span>|</span>
                <span className="text-primary cursor-pointer">
                    <i className="mdi mdi-link-variant me-1"></i>ID da venda: {row.idSale || row.id?.substring(0, 6)}
                </span>
            </div>
            <div className="mt-2 d-flex justify-content-between align-items-center border-top pt-2">
                <span className="fw-semibold font-size-13 text-dark neo-font-outfit">
                    Serviço/Produto — {row.description?.toUpperCase()}
                </span>
                <span className="fw-bold font-size-14 text-dark neo-font-outfit">
                    {formatCurrency(row.netAmount)}
                </span>
            </div>
        </div>
    );



    return (
        <NeoCard
            noBorder={false}
            className="neo-cashier-card mt-2 mb-4 animate__animated animate__fadeIn"
            bodyClassName="p-0"
        >
            {/* ═══ ACCORDION HEADER (SHIFT INFO) ═══ */}
            <div
                className={`neo-cashier-header ${isOpen ? 'neo-cashier-header-open' : ''}`}
                onClick={toggleOpen}
            >
                <div className="d-flex align-items-center">
                    <i className={`mdi mdi-chevron-${isOpen ? 'down' : 'right'} font-size-22 text-muted me-3`} />
                    <div>
                        <h5 className="mb-0 text-dark fw-bold neo-font-outfit font-size-16">{userName}</h5>
                        <div className="neo-cashier-shift-info">
                            <span><i className="mdi mdi-lock-open-outline me-1 text-primary"></i><strong>Abertura:</strong> {openDate} 07:17</span>
                            <span><i className="mdi mdi-cash-multiple me-1 text-muted"></i><strong>Valor Inicial:</strong> {formatCurrency(currentShift?.openingBalance || 0)}</span>
                            <span><i className="mdi mdi-arrow-up-bold-circle-outline me-1 text-success"></i><strong>Recebimentos:</strong> {formatCurrency(summary.totalIncome)}</span>
                            <span><i className="mdi mdi-arrow-down-bold-circle-outline me-1 text-danger"></i><strong>Saídas:</strong> {formatCurrency(summary.totalExpense)}</span>
                            <span><i className="mdi mdi-equal-box me-1 text-info"></i><strong>Saldo do Turno:</strong> {formatCurrency(summary.balanceOfDay)}</span>
                        </div>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                    {currentShift && currentShift.status === "OPEN" && (
                        <span
                            className="neo-cashier-close-link"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onCloseCashier) onCloseCashier();
                            }}
                        >
                            <i className="mdi mdi-lock me-1"></i>Fechar caixa
                        </span>
                    )}
                    {currentShift && currentShift.status === "CLOSED" && (
                        <NeoBadge
                            color="danger"
                            className="font-size-11 px-2.5 py-1 text-uppercase"
                        >
                            CAIXA FECHADO
                        </NeoBadge>
                    )}
                    <h5 className="mb-0 fw-bold text-dark neo-font-outfit neo-cashier-total">
                        {formatCurrency((currentShift?.openingBalance || 0) + summary.balanceOfDay)}
                    </h5>
                </div>
            </div>

            {/* ═══ TABLE CONTENT (NeoFinancialTable) ═══ */}
            {isOpen && (
                <div className="animate__animated animate__fadeIn">
                    <NeoFinancialTable
                        className="no-card"
                        columns={cashierColumns}
                        data={filteredRecords}
                        keyField="id"
                        loading={loading}
                        renderRowDetails={renderRowDetails}
                        searchValue={cashierSearch}
                        onSearchChange={setCashierSearch}
                        searchPlaceholder="Buscar movimentação..."
                        defaultItemsPerPage={20}
                        itemsPerPageOptions={[10, 20, 50]}
                        emptyMessage="Nenhuma movimentação registrada"
                        emptySubMessage="Registre vendas ou lançamentos para visualizar as movimentações do caixa."
                    />
                </div>
            )}
        </NeoCard>
    );
};
